import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap"
import { useAnnouncements } from "../../hooks/useAnnouncements"
import {
  ANNOUNCEMENT_DEFAULT_ICONS,
  ANNOUNCEMENT_DOT_GAP,
  ANNOUNCEMENT_DOT_SIZE,
  ANNOUNCEMENT_DOT_WINDOW,
  ANNOUNCEMENT_FADE_DURATION,
  ANNOUNCEMENT_SEVERITY_COLORS,
} from "../../constants/announcements"

const FALLBACK_SEVERITY = "INFO"

/** Duyurunun ikon adı ve vurgu rengi; bilinmeyen severity INFO'ya düşer. */
const resolveLook = (announcement) => {
  const severity = ANNOUNCEMENT_SEVERITY_COLORS[announcement?.severity]
    ? announcement.severity
    : FALLBACK_SEVERITY
  return {
    accent: ANNOUNCEMENT_SEVERITY_COLORS[severity],
    icon: announcement?.icon || ANNOUNCEMENT_DEFAULT_ICONS[severity],
  }
}

/** Bir noktadan diğerine kayma mesafesi. */
const DOT_STEP = ANNOUNCEMENT_DOT_SIZE + ANNOUNCEMENT_DOT_GAP
/** Pencerenin sabit genişliği: 3 nokta + aralarındaki 2 boşluk. */
const DOT_WINDOW_WIDTH =
  ANNOUNCEMENT_DOT_WINDOW * ANNOUNCEMENT_DOT_SIZE + (ANNOUNCEMENT_DOT_WINDOW - 1) * ANNOUNCEMENT_DOT_GAP

/**
 * Üçlü pencerenin ilk nokta indeksi. Aktif nokta mümkün olduğunca ortada
 * durur; baştaki ve sondaki duyurularda pencere sınıra yaslanır.
 */
const dotWindowStart = (index, total) =>
  Math.min(Math.max(index - 1, 0), Math.max(total - ANNOUNCEMENT_DOT_WINDOW, 0))

/** SPA içi rota mı, dış adres mi. Boş/eksik adres zaten çağrılmadan elenir. */
const isInternal = (url) => url.startsWith("/")

/**
 * Kenar/köşe ölçüleri dashboard'daki "Kontrol Paneli" kartıyla aynı (20px köşe,
 * 1.5rem kenar boşluğu); zemin ise beyaz kart — panelin diğer kartlarıyla aynı
 * gölge. Dashboard kartının kendisine dokunulmadı.
 *
 * Şerit ASLA iki satıra çıkmaz: uzun metin gelince nokta göstergeleri ve aksiyon
 * butonu alt satıra kaymak yerine yerlerinde kalır, taşan metin "..." ile
 * kesilir. Bunu sağlayan üç şey: dış kapsayıcıda flex-nowrap, metin kolonunda
 * min-width:0 (aksi hâlde flex öğesi içeriğinden dar olamaz, kısalmaz) ve sağ
 * bloktaki flex-shrink:0.
 */
const styles = `
  /* #page-topbar'ın (70px, .navbar-header flex) içinde bir öğe olarak durur.
     Akışta olması bilinçli: sidebar daraldığında/genişlediğinde şerit
     kendiliğinden kayar, sabit konumlandırma iki durum için ayrı hesap
     isterdi. flex-shrink açık ve min-width:0 — yer daralınca sağdaki menü
     öğelerini dışarı itmek yerine şerit kısalır. */
  .announcement-banner {
    /* Dashboard'daki "%0" rozetinin zemini: Bootstrap bg-warning-subtle.
       Temanın $warning'inden türüyor (--bs-warning-bg-subtle). Değişken yerine
       sabit yazıldı: koyu temada bu değişken #664c21'e dönüyor ve üstündeki
       lacivert metin okunmaz hâle gelirdi. */
    background: #ffe5ba;
    border-radius: 8px;
    /* Çerçeve header'daki "Link ile Tahsilat" butonuyla aynı turuncu:
       beyaz header üstünde beyaz kart yalnız gölgeyle zor seçiliyordu. */
    border: 1px solid #FFA500;
    /* Genişlik SABİT: flex-basis yüzde, flex-grow 0. Daha önce basis 'auto'
       olduğu için genişlik içerikten geliyordu ve uzun başlıklı duyuruda şerit
       sağa doğru büyüyordu. Artık hangi duyuru dönerse dönsün ölçü aynı; taşan
       metin '...' ile kesilir. flex-shrink açık kalır ki dar ekranda sağdaki
       menü öğelerini dışarı itmesin. */
    flex: 0 1 43%;
    min-width: 0;
    /* Sol kenar SABİT. .navbar-header space-between olduğu için artan genişlik
       şeridin iki yanına dağılıyor ve pencere büyüdükçe sol kenar sağa kayıyordu.
       margin-right:auto ile boşluğun tamamını sağ taraf yutar, şerit hamburger
       butonunun hemen sağında sabitlenir. Soldaki ince pay içerik kolonuyla
       (lacivert Kontrol Paneli kartı) hizayı tutturur. */
    margin-left: var(--ab-left-offset, 4px);
    margin-right: auto;
  }

  /* Metin alanı her duyuruda detay pop-up'ını açar; adres varsa ona gitme işi
     butonundur. Geri bildirim yalnız metinde: şeridin tamamını boyamak,
     butona uzanırken de tıklanabilir izlenimi veriyordu. */
  .announcement-banner .ab-text:hover .ab-title { text-decoration: underline; }

  /* Dar ekranda header zaten kalabalık; şerit tamamen gizlenir. */
  @media (max-width: 991.98px) {
    .announcement-banner { display: none !important; }
  }

  .announcement-banner .ab-icon-box {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* Metin kolonu: kısalabilmesi için min-width sıfırlanır. */
  .announcement-banner .ab-text,
  .announcement-banner .ab-fade,
  .announcement-banner .ab-title,
  .announcement-banner .ab-body {
    min-width: 0;
  }
  /* Yer daralınca ÖNCE açıklama feda edilir, başlık tam kalır.
     Başlık: flex-shrink 0 — hiç kısalmaz. Eskiden 0 1 auto idi, yani gövdeyle
     birlikte o da eziliyordu; "Detay" butonu her duyuruda çıkmaya başlayınca
     metin alanı 82px daraldı ve başlık tek harfe kadar iniyordu.
     max-width kapsayıcıyı aşmasını engeller: başlık tek başına satırdan uzunsa
     (150 karaktere kadar olabilir) buton ve noktaları dışarı itmek yerine
     kendisi "..." ile kesilir — son çare.
     Gövde: flex-basis 0 — boşluğu ilk o bırakır. */
  .announcement-banner .ab-title { flex: 0 0 auto; max-width: 100%; color: #0A316E; font-weight: 700; }
  .announcement-banner .ab-body  { flex: 1 1 0; color: rgba(10, 49, 110, .72); }

  /* Uzun metin: kesip "..." koymak yerine soldan sağa kaydırılır. Görüş
     penceresi taşmayı gizler, içerideki blok translate ile yürür. Hız sabit
     (px/sn) — uzun metin daha uzun sürer, kısa metin gereksiz beklemez.
     Kayma bitince bileşen sıradaki duyuruya geçer. */
  .announcement-banner .ab-marquee-viewport {
    overflow: hidden;
    flex: 1 1 0;
    min-width: 0;
  }
  .announcement-banner .ab-marquee-track {
    display: inline-flex;
    flex-wrap: nowrap;
    align-items: baseline;
    gap: .4rem;
    white-space: nowrap;
    animation: ab-marquee var(--ab-marquee-duration, 8s) linear var(--ab-marquee-delay, 1200ms) 1 both;
  }
  /* Fare şeritteyken ya da pop-up açıkken kayma durur: kullanıcı okurken
     metnin altından kaçması en sinir bozucu davranış. */
  .announcement-banner.is-paused .ab-marquee-track { animation-play-state: paused; }

  @keyframes ab-marquee {
    from { transform: translateX(0); }
    to   { transform: translateX(calc(-1 * var(--ab-marquee-distance, 0px))); }
  }

  /* Metin alanı artık her duyuruda tıklanabilir: detay pop-up'ını açar.
     Buton ayrı öğe olduğu için adrese gitme davranışı bundan etkilenmez. */
  .announcement-banner .ab-text { cursor: pointer; }

  .announcement-banner .ab-fade {
    /* cubic-bezier: hızlı başlayıp yavaşça oturan eğri (ease-out'tan daha
       yumuşak); 'both' ile animasyon başlamadan da başlangıç durumunda durur,
       böylece ilk karede içerik zıplamaz. */
    animation: ab-fade-in ${ANNOUNCEMENT_FADE_DURATION}ms cubic-bezier(.22, .61, .36, 1) both;
    will-change: opacity, transform;
  }
  @keyframes ab-fade-in {
    from { opacity: 0; transform: translateY(5px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .announcement-banner .ab-dot {
    width: ${ANNOUNCEMENT_DOT_SIZE}px;
    height: ${ANNOUNCEMENT_DOT_SIZE}px;
    flex-shrink: 0;
    padding: 0;
    border: 0;
    border-radius: 999px;
    background: #0A316E;
    opacity: .35;
    transition: opacity .2s, transform .2s;
  }
  .announcement-banner .ab-dot:hover { opacity: .7; }
  .announcement-banner .ab-dot.is-active { opacity: 1; transform: scale(1.15); }

  /* Sağ blok SABİT: metin uzunluğu değişse de buton ve noktalar kımıldamaz.
     Yuvalar sabit genişlikte ve flex-shrink:0 — böylece uzun/kısa duyuru
     arasında geçerken hiçbir şey yer değiştirmez. */
  .announcement-banner .ab-action-slot {
    width: 82px;
    flex-shrink: 0;
    display: flex;
  }
  .announcement-banner .ab-action {
    width: 100%;
    border-radius: 6px;
    font-weight: 600;
    white-space: nowrap;
    font-size: .72rem;
    padding: .2rem .5rem;
    line-height: 1.2;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }
  /* Pencere sabit genişlikte ve taşanı kırpar; şerit içeride kayar. Böylece
     duyuru sayısı kaç olursa olsun gösterge aynı yeri kaplar. */
  .announcement-banner .ab-dots {
    width: ${DOT_WINDOW_WIDTH}px;
    flex-shrink: 0;
    overflow: hidden;
    display: flex;
    align-items: center;
  }
  .announcement-banner .ab-dots-track {
    display: flex;
    align-items: center;
    gap: ${ANNOUNCEMENT_DOT_GAP}px;
    transition: transform ${ANNOUNCEMENT_FADE_DURATION}ms cubic-bezier(.22, .61, .36, 1);
  }

  /* Detay pop-up'ı: süper admin metni bir textarea'ya yazıyor, yani paragraf
     ve satır araları anlamlı. pre-wrap ile yazıldığı gibi görünür; wrap-anchor
     olmadan uzun bir URL yapıştırıldığında gövde yatay taşardı. */
  .announcement-detail-body {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  @media (prefers-reduced-motion: reduce) {
    .announcement-banner .ab-fade { animation: none; }
    /* Kaydırma yapılmaz; metin kesilir ve rotasyon normal süreyle döner. */
    .announcement-banner .ab-marquee-track { animation: none; }
    .announcement-banner .ab-dot { transition: none; }
    .announcement-banner .ab-dots-track { transition: none; }
  }
`

/**
 * Panelin içerik alanının üstündeki duyuru şeridi.
 *
 * Sayfa başına eklenmez — VerticalLayout'ta bir kez durur. Yayında duyuru
 * yoksa hiçbir şey çizmez (null döner), böylece şeridin kapladığı yer de
 * oluşmaz. Rotasyon/zamanlama mantığı useAnnouncements içinde.
 */
/** Kaydırma hızı: saniyede kaç piksel. Okunabilirlik ile beklenen süre dengesi. */
const MARQUEE_SPEED_PX_PER_SEC = 45
/** Kayma bitince sıradakine geçmeden önceki nefes payı. */
const MARQUEE_END_PAUSE_MS = 700

const AnnouncementBanner = () => {
  const { items, current, index, goTo, pause, resume, paused, setHoldRotation } = useAnnouncements()

  // Metin kapsayıcısına sığıyor mu? Sığmıyorsa taşma kadar kaydırılır.
  const viewportRef = useRef(null)
  const trackRef = useRef(null)
  const endTimerRef = useRef(null)
  const [marqueeDistance, setMarqueeDistance] = useState(0)
  // Tek duyuru varken kayma başa sarsın diye: değişince track yeniden kurulur.
  const [marqueeCycle, setMarqueeCycle] = useState(0)

  /*
   * Pop-up açılırken duyurunun KOPYASI saklanır, `current` okunmaz: fare
   * şeritten çıkınca resume tetiklenir ve rotasyon devam ederdi; kullanıcı
   * okurken metin altından değişirdi. Snapshot bunu kökten keser.
   */
  const [detailItem, setDetailItem] = useState(null)

  const closeDetail = () => {
    setDetailItem(null)
    resume()
  }

  /*
   * Taşma ölçümü render'dan SONRA, boyama ÖNCESİ yapılır (useLayoutEffect):
   * useEffect ile yapılsaydı metin bir kare "..." ile kesik görünüp sonra
   * kayan hâle geçerdi. Ölçüm her duyuru değişiminde yenilenir.
   */
  useLayoutEffect(() => {
    const viewport = viewportRef.current
    const track = trackRef.current
    if (!viewport || !track) {
      setMarqueeDistance(0)
      return
    }
    const overflow = track.scrollWidth - viewport.clientWidth
    // Birkaç piksellik fark yuvarlama hatasıdır; onun için animasyon açmayız.
    setMarqueeDistance(overflow > 4 ? overflow : 0)
  }, [current?.id, items.length])

  // Kayma sürerken otomatik rotasyon beklemede: sırayı animasyon bitirince verir.
  useEffect(() => {
    setHoldRotation(marqueeDistance > 0)
    return () => setHoldRotation(false)
  }, [marqueeDistance, setHoldRotation])

  /** Kayma bittiğinde sıradaki duyuruya geç; tek duyuru varsa başa sarar. */
  const handleMarqueeEnd = useCallback(() => {
    clearTimeout(endTimerRef.current)
    endTimerRef.current = setTimeout(() => {
      if (items.length < 2) {
        setMarqueeCycle((c) => c + 1)
      } else {
        goTo(index + 1)
      }
    }, MARQUEE_END_PAUSE_MS)
  }, [items.length, goTo, index])

  // Bileşen sökülürse bekleyen geçiş iptal edilir.
  useEffect(() => () => clearTimeout(endTimerRef.current), [])

  if (!current) return null

  const { accent, icon } = resolveLook(current)
  const detailLook = resolveLook(detailItem)

  const hasAction = Boolean(current.actionUrl) && Boolean(current.actionLabel)
  const hasRotation = items.length > 1

  const openDetail = () => {
    pause()
    setDetailItem(current)
  }

  // Aksiyon yuvası artık HER duyuruda dolu: adresi olan duyuruda yönlendirme
  // butonu, olmayanda metni pop-up'ta açan "Detay" butonu. Bu yüzden eskiden
  // yuvayı koşullu ayıran `reserveActionSlot` hesabına gerek kalmadı — yuva
  // her dönüşte aynı yeri kapladığı için noktalar da kımıldamıyor.

  // Turuncu zeminde önem derecesi rengi okunmaz (WARNING zeminle aynı tona
  // düşer); buton header'daki "Anlık Tahsilat" ile aynı laciverte sabitlenir.
  const actionStyle = { backgroundColor: "#0A316E", borderColor: "#0A316E", color: "#fff" }

  return (
    <>
    <div
      className={`announcement-banner d-flex flex-nowrap align-items-center gap-2 px-3 py-1${paused ? " is-paused" : ""}`}
      style={{ fontSize: ".82rem" }}
      /* Şeridin tamamı pop-up'ı açar. Klavye yolu bilinçli olarak buradan
         geçmiyor: kapsayıcıya role="button" vermek hem içindeki gerçek
         butonları iç içe geçmiş etkileşimli öğe hâline getirir hem de metin
         alanındaki aria-live'ı ezerdi. Klavye kullanıcısı "Detay" butonunu
         kullanır; buradaki tıklama fare için bir kolaylık. */
      /* Tıklama artık kapsayıcıda değil metin alanında: adresi olan duyuruda
         metin detayı açar, buton adrese gider. */
      // Kullanıcı şeritle ilgilenirken duyuru altından kaymasın: aksi hâlde
      // "Detay"a uzanırken hedef değişir ve yanlış bağlantıya tıklanır.
      onMouseEnter={pause}
      onMouseLeave={() => { if (!detailItem) resume() }}
      onFocus={pause}
      onBlur={() => { if (!detailItem) resume() }}
    >
      <style>{styles}</style>

      {/* Otomatik değişen içerik: ekran okuyucuya nazikçe bildirilir. */}
      <div
        className="ab-text d-flex flex-nowrap align-items-center flex-grow-1"
        style={{ gap: ".5rem" }}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        onClick={openDetail}
      >
        <div className="ab-icon-box rounded-3" style={{ background: "#fff" }}>
          <i className={`bx ${icon}`} style={{ fontSize: "1rem", color: accent }} />
        </div>

        {/* key: duyuru değişince hem solma hem kayma animasyonu baştan başlar. */}
        <div key={`${current.id}-${marqueeCycle}`} className="ab-fade ab-marquee-viewport" ref={viewportRef}>
          <div
            ref={trackRef}
            className={marqueeDistance > 0 ? "ab-marquee-track" : "d-flex flex-nowrap align-items-baseline"}
            style={
              marqueeDistance > 0
                ? {
                    "--ab-marquee-distance": `${marqueeDistance}px`,
                    "--ab-marquee-duration": `${(marqueeDistance / MARQUEE_SPEED_PX_PER_SEC).toFixed(2)}s`,
                  }
                : { gap: ".4rem" }
            }
            onAnimationEnd={marqueeDistance > 0 ? handleMarqueeEnd : undefined}
          >
            <span className={`ab-title${marqueeDistance > 0 ? "" : " text-truncate"}`}>{current.title}</span>
            <span className={`ab-body${marqueeDistance > 0 ? "" : " text-truncate"}`}>{current.body}</span>
          </div>
        </div>
      </div>

      <div className="d-flex flex-nowrap align-items-center flex-shrink-0" style={{ gap: ".75rem" }}>
        <div className="ab-action-slot">
          {hasAction ? (
            isInternal(current.actionUrl) ? (
              <Link to={current.actionUrl} className="btn btn-sm ab-action" style={actionStyle}>
                <span className="text-truncate">{current.actionLabel}</span>
                <i className="bx bx-right-arrow-alt ms-1" />
              </Link>
            ) : (
              <a
                href={current.actionUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm ab-action"
                style={actionStyle}
              >
                <span className="text-truncate">{current.actionLabel}</span>
                <i className="bx bx-link-external ms-1" />
              </a>
            )
          ) : (
            /* Adresi olmayan duyuru: şeritte kırpılan metin burada tam okunur.
               Yönlendirmeli duyuruda gerekmiyor, zaten hedef sayfaya gidiyor. */
            <button
              type="button"
              className="btn btn-sm ab-action"
              style={actionStyle}
              onClick={(e) => {
                // Kök zaten aynı işi yapıyor; iki kez çalışmasın.
                e.stopPropagation()
                openDetail()
              }}
            >
              <span className="text-truncate">Detay</span>
              <i className="bx bx-chevron-right ms-1" />
            </button>
          )}
        </div>

        {hasRotation && (
          <div className="ab-dots">
            <div
              className="ab-dots-track"
              style={{ transform: `translateX(-${dotWindowStart(index, items.length) * DOT_STEP}px)` }}
            >
              {items.map((item, i) => (
                <button
                  key={item.id}
                  type="button"
                  className={`ab-dot${i === index ? " is-active" : ""}`}
                  onClick={(e) => {
                    // Duyurular arasında gezinmek pop-up açmamalı.
                    e.stopPropagation()
                    goTo(i)
                  }}
                  aria-label={`${i + 1}. duyuru`}
                  aria-current={i === index ? "true" : undefined}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>

    {/*
      Modal ŞERİDİN DIŞINDA duruyor. React sentetik olayları DOM ağacında
      değil React ağacında kabarır: modal portala çizilse bile şeridin çocuğu
      olsaydı, içindeki her tıklama (Kapat dâhil) şeridin onClick'ine ulaşır
      ve pop-up kapanır kapanmaz yeniden açılırdı.
    */}
    <Modal isOpen={!!detailItem} toggle={closeDetail} centered scrollable>
      <ModalHeader toggle={closeDetail}>
        <i className={`bx ${detailLook.icon} me-2`} style={{ color: detailLook.accent }} />
        {detailItem?.title}
      </ModalHeader>

      <ModalBody>
        <div className="announcement-detail-body">{detailItem?.body}</div>
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={closeDetail}>Kapat</Button>
      </ModalFooter>
    </Modal>
    </>
  )
}

export default AnnouncementBanner
